// Presentation only. Native option IDs, selection, stock and form data are untouched.
(() => {
  const scores = new Map();
  const pending = new WeakSet();
  const luminance = ([r, g, b]) => {
    const linear = n => (n /= 255) <= .04045 ? n / 12.92 : ((n + .055) / 1.055) ** 2.4;
    return .2126 * linear(r) + .7152 * linear(g) + .0722 * linear(b);
  };
  function imageScore(url) {
    if (!scores.has(url)) scores.set(url, new Promise(resolve => {
      const image = new Image();
      image.crossOrigin = 'anonymous';
      image.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          canvas.width = canvas.height = 40;
          const ctx = canvas.getContext('2d', { willReadFrequently: true });
          // Sample the garment/accessory body, avoiding the white studio surround.
          ctx.drawImage(image, image.width * .3, image.height * .3,
            image.width * .4, image.height * .4, 0, 0, 40, 40);
          const pixels = ctx.getImageData(0, 0, 40, 40).data;
          const values = [];
          for (let i = 0; i < pixels.length; i += 4) {
            if (pixels[i + 3] > 200) values.push(luminance([pixels[i], pixels[i + 1], pixels[i + 2]]));
          }
          values.sort((a, b) => a - b);
          resolve(values.length ? values[Math.floor(values.length / 2)] : null);
        } catch { resolve(null); }
      };
      image.onerror = () => resolve(null);
      image.src = url;
    }));
    return scores.get(url);
  }
  async function score(input) {
    const rgb = input.dataset.colorRgb?.match(/[\d.]+/g)?.map(Number);
    if (rgb?.length === 3) return luminance(rgb);
    return input.dataset.colorImage ? imageScore(input.dataset.colorImage) : null;
  }
  async function sortGroup(group) {
    if (pending.has(group)) return;
    const inputs = [...group.querySelectorAll('[data-color-value]')];
    if (inputs.length < 2) return;
    pending.add(group);
    try {
      const entries = await Promise.all(inputs.map(async (input, index) => ({
        input, index, score: await score(input),
        node: input.tagName === 'OPTION' ? input :
          (input.closest('li') && group.contains(input.closest('li')) ? input.closest('li') : input.closest('label')),
      })));
      if (!group.isConnected || entries.some(e => e.score == null || !e.node)) return;
      const parent = entries[0].node.parentElement;
      if (entries.some(e => e.node.parentElement !== parent)) return;
      // Stable visual ordering also keeps equally bright patterns in their existing order.
      const anchorRank = entry => {
        const name = entry.input.dataset.colorValue.trim().toLowerCase();
        return name === 'white' ? -1 : name === 'black' ? 1 : 0;
      };
      entries.sort((a, b) => anchorRank(a) - anchorRank(b) || b.score - a.score || a.index - b.index);
      const current = [...parent.children].filter(n => entries.some(e => e.node === n));
      if (entries.every((e, i) => e.node === current[i])) return;
      const active = document.activeElement;
      const selected = group.tagName === 'SELECT' ? group.value : null;
      const anchor = document.createComment('color options');
      parent.insertBefore(anchor, current[0]);
      for (const entry of entries) parent.insertBefore(entry.node, anchor);
      anchor.remove();
      if (selected !== null) group.value = selected;
      if (active && entries.some(e => e.node.contains(active))) active.focus({ preventScroll: true });
      group.dataset.colorOrder = 'light-to-dark';
    } finally { pending.delete(group); }
  }
  function scan(root) {
    const groups = new Set();
    if (root.matches?.('[data-color-value]')) groups.add(root.closest('fieldset,select'));
    for (const input of root.querySelectorAll('[data-color-value]')) groups.add(input.closest('fieldset,select'));
    for (const group of groups) if (group) sortGroup(group);
  }
  window.LaurelColorOrder = { score };
  function start() {
    scan(document);
    let scheduled = false;
    new MutationObserver(records => {
      if (!records.some(r => [...r.addedNodes].some(n => n.nodeType === 1)) || scheduled) return;
      scheduled = true;
      requestAnimationFrame(() => { scheduled = false; scan(document); });
    }).observe(document.body, { subtree: true, childList: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();
